import React, { useState, useEffect } from 'react';
import DesktopIcon from './DesktopIcon';
import AboutWindow from './AboutWindow';
import ResumeWindow from './SkillsWindow';
import RepositoriesWindow from './RepositoriesWindow';
import EnvironmentsWindow from './EnvironmentsWindow';
import EmailWindow from './EmailWindow';
import ProjectsWindow from './ProjectsWindow';
import ExperienceWindow from './ExperienceWindow';
import EducationWindow from './EducationWindow';
import ProjectDetailWindow from './ProjectDetailWindow';
import ExperienceDetailWindow from './ExperienceDetailWindow';
import Taskbar from './Taskbar';
import TEXT from '../constants/text';
import {
  trackWindowOpen,
  trackWindowClose,
  trackWindowFocus,
  startWindowEngagement,
  stopWindowEngagement,
} from '../analytics';
/* main desktop component - handles all the windows and icons */

const Desktop = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [openWindows, setOpenWindows] = useState({
    about: false,
    skills: false,
    repositories: false,
    environments: false,
    email: false,
    projects: false,
    experience: false,
    education: false,
    projectDetail: false,
    experienceDetail: false
  });
  /* track which windows are open */
  
  const [windowZIndex, setWindowZIndex] = useState({
    about: 10,
    skills: 10,
    repositories: 10,
    environments: 10,
    email: 10,
    projects: 10,
    experience: 10,
    education: 10,
    projectDetail: 10,
    experienceDetail: 10
  });
  /* track z-index for each window */
  
  const [nextZIndex, setNextZIndex] = useState(11);
  /* counter for next z-index value */
  
  const [projectDetailData, setProjectDetailData] = useState(null);
  /* store project detail data for the detail window */
  
  const [experienceDetailData, setExperienceDetailData] = useState(null);
  /* store experience detail data for the detail window */

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 770);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const openWindow = (windowName) => {
    // For mobile: redirect to PDF for resume, otherwise open window
    if (isMobile && windowName === 'skills') {
      window.open('/assets/Avo_s_Resume.pdf', '_blank');
      return;
    }
    
    // For mobile: close all other windows before opening new one
    if (isMobile) {
      setOpenWindows(prev => {
        const newState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = false;
        });
        newState[windowName] = true;
        return newState;
      });
    } else {
      setOpenWindows(prev => ({
        ...prev,
        [windowName]: true
      }));
    }
    /* open a window when you click an icon */
    
    // Track window opening
    trackWindowOpen(windowName);
    startWindowEngagement(windowName);
    
    // bring window to front
    setWindowZIndex(prev => ({
      ...prev,
      [windowName]: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
    /* bring new window to front with highest z-index */
  };

  const closeWindow = (windowName) => {
    // Track window closing
    stopWindowEngagement(windowName);
    trackWindowClose(windowName);
    
    // find the window element and trigger its close animation
    const windowElement = document.querySelector(`[data-window="${windowName}"]`);
    if (windowElement) {
      // trigger the window's own close animation
      windowElement.classList.add('closing');
      const done = () => {
        windowElement.removeEventListener('animationend', done);
        setOpenWindows(prev => ({
          ...prev,
          [windowName]: false
        }));
      };
      windowElement.addEventListener('animationend', done, { once: true });
    } else {
      // fallback: close immediately if element not found
      setOpenWindows(prev => ({
        ...prev,
        [windowName]: false
      }));
    }
    /* close a window with animation */
  };

  const focusWindow = (windowName) => {
    // Track window focus
    trackWindowFocus(windowName);
    
    // bring window to front
    setWindowZIndex(prev => ({
      ...prev,
      [windowName]: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
    /* bring clicked window to front with highest z-index */
  };

  const closeAllWindows = () => {
    // close all open windows
    Object.keys(openWindows).forEach(windowName => {
      if (openWindows[windowName]) {
        // map window names to their data-window attributes
        const dataWindowMap = {
          projectDetail: 'project-detail',
          experienceDetail: 'experience-detail'
        };
        
        const dataWindowName = dataWindowMap[windowName] || windowName;
        const windowElement = document.querySelector(`[data-window="${dataWindowName}"]`);
        
        if (windowElement) {
          windowElement.classList.add('closing');
          const done = () => {
            windowElement.removeEventListener('animationend', done);
            setOpenWindows(prev => ({
              ...prev,
              [windowName]: false
            }));
          };
          windowElement.addEventListener('animationend', done, { once: true });
        } else {
          // if no element found, just close the window state
          setOpenWindows(prev => ({
            ...prev,
            [windowName]: false
          }));
        }
      }
    });
    /* close all windows with animation */
  };

  const getOpenWindowCount = () => {
    return Object.values(openWindows).filter(isOpen => isOpen).length;
    /* count how many windows are currently open */
  };
  
  const handleProjectClick = (projectData) => {
    setProjectDetailData(projectData);
    
    // For mobile: close all other windows before opening detail window
    if (isMobile) {
      setOpenWindows(prev => {
        const newState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = false;
        });
        newState.projectDetail = true;
        return newState;
      });
    } else {
      setOpenWindows(prev => ({
        ...prev,
        projectDetail: true
      }));
    }
    
    setWindowZIndex(prev => ({
      ...prev,
      projectDetail: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
  };
  /* handle clicking on a project item to open detail window */
  
  const handleExperienceClick = (experienceData) => {
    setExperienceDetailData(experienceData);
    
    // For mobile: close all other windows before opening detail window
    if (isMobile) {
      setOpenWindows(prev => {
        const newState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = false;
        });
        newState.experienceDetail = true;
        return newState;
      });
    } else {
      setOpenWindows(prev => ({
        ...prev,
        experienceDetail: true
      }));
    }
    
    setWindowZIndex(prev => ({
      ...prev,
      experienceDetail: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
  };
  /* handle clicking on an experience item to open detail window */

  const hireMeParty = () => {
    const colors = ['red', 'orange', 'yellow', 'green', 'blue', 'indigo', 'violet'];
    /* easter egg function - makes the screen flash colors */
    for (let i = 0; i < 200; i++) {
      const text = document.createElement('div');
      text.innerText = 'HIRE ME';
      text.style.position = 'fixed';
      text.style.left = `${Math.random() * 200}%`;
      text.style.top = `${Math.random() * 200}%`;
      text.style.fontSize = `${Math.random() * 50 + 20}px`;
      text.style.fontWeight = 'bold';
      text.style.color = colors[Math.floor(Math.random() * colors.length)];
      text.style.animation = 'flashy 0.1s infinite alternate, pulse 1s ease-in-out';
      text.style.zIndex = 9999;
      document.body.appendChild(text);
      /* create random "hire me" text elements */

      setTimeout(() => text.remove(), 5000);
      /* remove them after 5 seconds */
    }
  };

  return (
    <div>
      
      {/* desktop icons container - visible on all devices */}
      <div className="desktop-icons-container">
          <DesktopIcon
            icon="/assets/agentVo.png"
            label={TEXT.DESKTOP_ICONS.ABOUT}
            onClick={() => openWindow('about')}
            data-analytics-id="icon_about"
          />
          {/* about me icon */}
          
          <DesktopIcon
            icon="/assets/experience-icon.png"
            label={TEXT.DESKTOP_ICONS.EXPERIENCE}
            onClick={() => openWindow('experience')}
            data-analytics-id="icon_experience"
          />
          {/* experience icon */}
          
          <DesktopIcon
            icon="/assets/pixelConsole.png"
            label={TEXT.DESKTOP_ICONS.PROJECTS}
            onClick={() => openWindow('projects')}
            data-analytics-id="icon_projects"
          />
          {/* projects icon */}
          
          <DesktopIcon
            icon="/assets/resumeIcon.png"
            label={TEXT.DESKTOP_ICONS.SKILLS}
            onClick={() => openWindow('skills')}
            data-analytics-id="icon_resume"
          />
          {/* resume icon */}
          
          <DesktopIcon
            icon="/assets/pixelCertificate.png"
            label={TEXT.DESKTOP_ICONS.EDUCATION}
            onClick={() => openWindow('education')}
            data-analytics-id="icon_education"
          />
          {/* education icon */}
          
          <DesktopIcon
            icon="/assets/party-icon.png"
            label={TEXT.DESKTOP_ICONS.PARTY}
            onClick={hireMeParty}
            data-analytics-id="icon_party"
          />
          {/* easter egg party button */}
          
        </div>
      {/* desktop icons container - prevents overlap */}

      {/* windows */}
      {openWindows.about && (
        <AboutWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              about: false
            }));
          }}
          zIndex={windowZIndex.about}
          onFocus={() => focusWindow('about')}
        />
      )}
      {/* about window */}
      
      {openWindows.skills && (
        <ResumeWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              skills: false
            }));
          }}
          zIndex={windowZIndex.skills}
          onFocus={() => focusWindow('skills')}
        />
      )}
      {/* resume window */}
      
      {openWindows.repositories && (
        <RepositoriesWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              repositories: false
            }));
          }}
          zIndex={windowZIndex.repositories}
          onFocus={() => focusWindow('repositories')}
        />
      )}
      {/* repos window */}
      
      {openWindows.environments && (
        <EnvironmentsWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              environments: false
            }));
          }}
          zIndex={windowZIndex.environments}
          onFocus={() => focusWindow('environments')}
        />
      )}
      {/* environments window */}
      
      {openWindows.email && (
        <EmailWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              email: false
            }));
          }}
          zIndex={windowZIndex.email}
          onFocus={() => focusWindow('email')}
        />
      )}
      {/* email window */}
      
      {openWindows.projects && (
        <ProjectsWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              projects: false
            }));
          }}
          zIndex={windowZIndex.projects}
          onFocus={() => focusWindow('projects')}
          onProjectClick={handleProjectClick}
        />
      )}
      {/* projects window */}
      
      {openWindows.experience && (
        <ExperienceWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              experience: false
            }));
          }}
          zIndex={windowZIndex.experience}
          onFocus={() => focusWindow('experience')}
          onExperienceClick={handleExperienceClick}
          openDetailWindow={openWindows.experienceDetail}
        />
      )}
      {/* experience window */}
      
      {openWindows.education && (
        <EducationWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              education: false
            }));
          }}
          zIndex={windowZIndex.education}
          onFocus={() => focusWindow('education')}
        />
      )}
      {/* education window */}
      
      {openWindows.projectDetail && (
        <ProjectDetailWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              projectDetail: false
            }));
          }}
          zIndex={windowZIndex.projectDetail}
          onFocus={() => focusWindow('projectDetail')}
          projectData={projectDetailData}
        />
      )}
      {/* project detail window */}
      
      {openWindows.experienceDetail && (
        <ExperienceDetailWindow 
          onClose={() => {
            setOpenWindows(prev => ({
              ...prev,
              experienceDetail: false
            }));
          }}
          zIndex={windowZIndex.experienceDetail}
          onFocus={() => focusWindow('experienceDetail')}
          experienceData={experienceDetailData}
        />
      )}
      {/* experience detail window */}

      {/* taskbar */}
      <Taskbar 
        openWindows={openWindows}
        onWindowClick={openWindow}
        onWindowClose={closeWindow}
        onCloseAll={closeAllWindows}
        openWindowCount={getOpenWindowCount()}
        projectDetailData={projectDetailData}
        experienceDetailData={experienceDetailData}
      />
      {/* the bottom taskbar thing */}
    </div>
  );
};

export default Desktop;
